import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ItemPacking, ItemType, VehicleTypes } from '@hero/schema';
import { resolveLoadPackingAndQuantityFromVehicles } from '@hero/core';
import { usePublishOrderStore } from '@hero/hooks-shared';
import { useSaveOrderDefaultsManager } from '@hero/web-hooks';
import { CheckIcon } from '@hero/web-icons';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../../ui/Sheet/Sheet';
import { Button } from '../../../../ui/Button/Button';
import {
  LoadDetailsFields,
  type LoadDetailsFieldsValue,
} from '../../Load/LoadDetailsFields';

export interface LoadDetailsFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Rich load-details drawer composed of the same shared
 * `LoadDetailsFields` block the order-drawer's edit-load surface
 * uses. The publish flow hides the pickup-date row (pickup is its
 * own dedicated step) and writes weight in tons to match the
 * existing edit-load behaviour.
 *
 * Every input writes through to the publish-order store on change —
 * no Save/Cancel commit step. The drawer's "Done" footer is purely a
 * convenience to close; the side panel reflects edits live.
 *
 * Phase 2 (relations-v2): every change goes through `setLoadFromUser`
 * (not `setLoad`) so the auto-apply manager
 * (`useApplyClientOrderDefaultsManager`) treats these as explicit user
 * decisions — switching the picked client later seeds defaults only
 * into fields the user hasn't touched here.
 */
export function LoadDetailsFormDrawer({ isOpen, onClose }: LoadDetailsFormDrawerProps) {
  const { t } = useTranslation(['m-order', 'c-main']);

  const load = usePublishOrderStore(s => s.load);
  const loadOverrides = usePublishOrderStore(s => s.loadOverrides);
  const setLoad = usePublishOrderStore(s => s.setLoad);
  const setLoadFromUser = usePublishOrderStore(s => s.setLoadFromUser);

  // Phase 2 (relations-v2) — "save these as the default for [client]"
  // banner. Only shown when the publish-page is being driven by a
  // business creator with a CRM Client picked AND the user has touched
  // any load-detail field; carriers + internal shipments + untouched
  // forms hide it. See `useSaveOrderDefaultsManager` for the full rule.
  const saveDefaults = useSaveOrderDefaultsManager();

  const handleSaveDefaults = async () => {
    try {
      await saveDefaults.saveForClient();
      toast.success(
        t('wizard.load.save_defaults.saved_toast', {
          defaultValue: 'Saved as default for {{name}}',
          name: saveDefaults.targetName,
        }),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(message);
    }
  };

  // Project the publish-store load shape onto the shared field
  // values. Vehicle types prefer the plural `vehicleTypes` array but
  // fall back to the singular for back-compat with code paths that
  // still write the legacy field.
  const value: LoadDetailsFieldsValue = {
    vehicleTypes: (load.vehicleTypes ??
      (load.vehicleType ? [load.vehicleType] : [])) as VehicleTypes[],
    temperatureType: load.temperatureType,
    itemType: load.itemType,
    packing: load.packing,
    quantity: load.quantity ?? 0,
    itemDescription: load.itemDescription ?? '',
    weight: load.weightTon,
    itemCost: load.itemCost,
    podSettings: load.podSettings,
  };

  // Translate the shared-field patch shape back onto the publish
  // store. The store's `syncVehicleTypeFields` helper keeps
  // `vehicleType` / `vehicleTypes` aligned automatically.
  const handleChange = (patch: Partial<LoadDetailsFieldsValue>) => {
    const userPatch: Record<string, unknown> = {};
    const autoPatch: Record<string, unknown> = {};

    if ('vehicleTypes' in patch) {
      userPatch.vehicleTypes = patch.vehicleTypes as VehicleTypes[];
      const derived = resolveLoadPackingAndQuantityFromVehicles(
        (patch.vehicleTypes ?? []) as VehicleTypes[],
      );
      if (!loadOverrides.packing) autoPatch.packing = derived.packing;
      if (!loadOverrides.quantity) autoPatch.quantity = derived.quantity;
    }
    if ('temperatureType' in patch) userPatch.temperatureType = patch.temperatureType;
    if ('itemType' in patch) userPatch.itemType = patch.itemType as ItemType | undefined;
    if ('packing' in patch) userPatch.packing = patch.packing as ItemPacking | undefined;
    if ('quantity' in patch) userPatch.quantity = patch.quantity;
    if ('itemDescription' in patch) userPatch.itemDescription = patch.itemDescription;
    if ('weight' in patch) userPatch.weightTon = patch.weight;
    if ('itemCost' in patch) userPatch.itemCost = patch.itemCost;
    if ('podSettings' in patch) userPatch.podSettings = patch.podSettings;

    if (Object.keys(autoPatch).length > 0) {
      setLoad(autoPatch);
    }
    if (Object.keys(userPatch).length > 0) {
      setLoadFromUser(userPatch);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 bg-white w-[480px] sm:max-w-[480px]">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>{t('form.load_details.title')}</SheetTitle>
          <SheetDescription>{t('form.load_details.description')}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6">
          <LoadDetailsFields
            value={value}
            onChange={handleChange}
            weightUnit="ton"
            showPodSettings
          />
        </div>

        <div className="border-t p-4 flex items-center justify-between gap-3">
          {saveDefaults.canSaveForClient || saveDefaults.isClientLoading ? (
            <button
              type="button"
              onClick={handleSaveDefaults}
              disabled={
                saveDefaults.isSaving ||
                saveDefaults.isClientLoading ||
                !saveDefaults.canSaveForClient
              }
              className="inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 disabled:opacity-50">
              <CheckIcon className="size-3.5" />
              {saveDefaults.isSaving || saveDefaults.isClientLoading
                ? t('wizard.load.save_defaults.saving', { defaultValue: 'Saving…' })
                : t('wizard.load.save_defaults.cta_for_client', {
                    defaultValue: 'Save as default for {{name}}',
                    name: saveDefaults.targetName,
                  })}
            </button>
          ) : (
            <span />
          )}
          <Button onClick={onClose}>{t('c-main:done', { defaultValue: 'Done' })}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
